import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule, DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTable, MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { AuthenticationService } from '../../services/authentication.service';

@Component({
  selector: 'app-shared-table',
  standalone: true,
  templateUrl: './shared-table.component.html',
  styleUrls: ['./shared-table.component.scss'],
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatIconModule,
    MatButtonModule,
    MatCardModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatTooltip,
    DragDropModule
]
})
export class SharedTableComponent {
  /* =========================
   * INPUTS
   * ========================= */
  @Input() dataSource: any = [];
  @Input() displayedColumns: string[] = [];
  @Input() columnLabels: Record<string, string> = {};

  @Input() notAllowEdit = false;
  @Input() notAllowDelete = false;
  @Input() notAllowAdd = false;

  @Input() isJobPendingFn!: (id: number) => boolean;
  @Input() isAnyJobPendingFn!: () => boolean;
  @Input() shouldDisableActionsOnRowFn!: (job: any) => boolean;

  @Input() enableRowClick = false;
  @Input() enableRowDrag = false;
  @Input() nameTable: string | null = null;
  @Input() isLoading: boolean = false;
  @Input() hasError: boolean = false;
  @Input() isSortable: boolean = true;

  /** Paginazione server-side */
  @Input() length = 0;
  @Input() pageSize = 10;
  @Input() pageIndex = 0;

  /* =========================
   * OUTPUTS
   * ========================= */
  @Output() rowClick = new EventEmitter<any>();
  @Output() rowMoved = new EventEmitter<{fromSorting: number, toSorting: number}>();
  @Output() pageChanged = new EventEmitter<PageEvent>();
  @Output() sortChanged = new EventEmitter<Sort>();
  @Output() openModal = new EventEmitter<{ data: any; type: 'add' | 'edit' | 'delete'; nameTable?: string }>();
  @Output() jobAction = new EventEmitter<{ data: any; type: 'start' | 'stop' }>();
  @Output() toolbarAction = new EventEmitter<'refresh' | 'stopAll' | 'export' | 'import'>();
  @Output() downloadAction = new EventEmitter<any>();
  @Output() scheduleAction = new EventEmitter<any>(); //azione nella tabella draft che permette di andare nel tab di scheudulazione 
  @Output() toggleActivationJobAction = new EventEmitter<any>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatTable) table!: MatTable<any>;

  private expandedRows: Set<any> = new Set<any>();

  constructor(private readonly snackBar: MatSnackBar, private readonly auth: AuthenticationService, private readonly datePipe: DatePipe) {}
  
  get isDragDisabled(): boolean {
    const data = this.dataSource.data || this.dataSource;
    const noData = !data || data.length === 0;
    return !this.enableRowDrag || this.isLoading || this.hasError || noData;
  }

  goToSchedule(element: any){
    this.scheduleAction.emit(element);
  }

  //Metodi per gestire l'espansione di un testo nella cella della tabella
  
  toggleExpand(element: any, event: Event){
    event.stopPropagation();
    if(this.expandedRows.has(element)){
      this.expandedRows.delete(element);
    } else {
      this.expandedRows.add(element);
    }
  }

  isExpanded(element: any): boolean {
    return this.expandedRows.has(element);
  }

  /* =========================
   * GET DATASOURCE
   * ========================= */
  get effectiveDatasource(): any[] {
    const noData = !this.dataSource.data || this.dataSource.data.length === 0;
    if(this.isLoading || this.hasError || noData){
      return [{}];
    }
    return this.dataSource;
  }
  
  /* =========================
   * SPANNING ROW (loading - empty - error)
   * ========================= */
  isSpanningRow = (_:number, __:any) => {
    const noData = !this.dataSource.data || this.dataSource.data.length === 0;
    return this.isLoading || this.hasError || noData;
  }

  /* =========================
   * HEADER TOOLBAR (actions)
   * ========================= */
  emitToolbarAction(action: 'refresh' | 'stopAll' | 'export' | 'import'): void {
    this.toolbarAction.emit(action);
  }

  emitDownloadAction(fileInfo: any): void {
    this.downloadAction.emit(fileInfo);
  }

  /** Richiesta di apertura modale: inoltra al parent */
  requestOpenDialog(row: any, type: 'add' | 'edit' | 'delete', nameTable?: string): void {
    this.openModal.emit({ data: row, type, nameTable});
    console.log('nome tabella', nameTable);
  }

  /* =========================
   * RIGA: azioni speciali crawling-web
   * ========================= */
  requestJobAction(row: any, type: 'start' | 'stop'): void {
    this.jobAction.emit({ data: row, type });
  }

  allowedStatusPlayIcon = ['FINISHED', 'FAILED', 'KILLED', 'SCHEDULED', 'IDLE', 'PAUSED'];

  allowedStatusStopIcon = ['RUNNING', 'STOPPING', 'KILLING', 'ANY'];

  toggleActivationJob(row: any, action: 'activate' | 'deactivate'){
    console.log('qui', row);
    this.toggleActivationJobAction.emit({data: row, action: action});
  }

  /* =========================
   * DIAGNOSTICS: copia URL
   * ========================= */
  async copyUrl(url?: string): Promise<void> {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      this.snackBar.open('URL copiato negli appunti', 'OK', { duration: 1800 });
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      ta.remove();
      this.snackBar.open('URL copiato', 'OK', { duration: 1800 });
    }
  }

  /* =========================
   * Sorting & Pagination
   * ========================= */
  onSortChanged(sort: Sort): void {
    this.sortChanged.emit(sort);
  }

  onPageEvent(ev: PageEvent): void {
    this.pageChanged.emit(ev); 
  }

  /* =========================
   * Row interactions
   * ========================= */
  onRowClick(row: any, _event?: MouseEvent): void {
    if (!this.enableRowClick) return;
  
    if(this.nameTable === 'crawling-web') {
      if(this.isJobPendingFn(row.id) || this.shouldDisableActionsOnRowFn(row)) {
        console.log('click riga ignorato: job pending o running');
        return;
      } 
    }
    
    this.rowClick.emit(row);
  }

  onRowDrop(event: CdkDragDrop<any[]>){
    if(event.previousIndex === event.currentIndex){
      return;
    }
    console.log('event', event);
    const fromSorting = event.previousIndex;
    const toSorting = event.currentIndex;
    
    console.log('datasource', this.dataSource);
    const dataArray = this.dataSource.data || this.dataSource;

    moveItemInArray(dataArray, fromSorting, toSorting);
    if(this.dataSource.data){
      this.dataSource.data = [...dataArray];
    } else {
       this.table.renderRows();
    }
   
    this.rowMoved.emit({fromSorting, toSorting});
  }

  onRowKeydown(row: any, event: KeyboardEvent): void {
    if (!this.enableRowClick) return;
    const target = event.target as HTMLElement;
    
    // se il focus è su un elemento interattivo, non viene eseguito il click sulla riga
    const isInteractive = target.closest('button, [mat-icon-button]');
    if(isInteractive) return;

    const key = event.key?.toLowerCase();
    if (key === 'enter' || key === ' ') {
      event.preventDefault();
      this.rowClick.emit(row);
    }
  }

  canDoActions(role: string): boolean {
    return this.auth.hasRole(role);
  }

  formatDate(date: string){
    if(!date) return '';
    return this.datePipe.transform(date, 'dd/MM/yyyy HH:mm') ?? '';
  }

  bytesToKB(size: string ) : string {
    const bytes = Number(size);
    return (bytes/1024).toFixed(2);
  }

}
